import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { 
  CreditCard, 
  Calendar, 
  DollarSign, 
  Shield, 
  AlertCircle,
  CheckCircle,
  Settings,
  Plus,
  Trash2,
  Edit
} from 'lucide-react';
import { tenantPortalAPI } from '../services/api';

const TenantAutopay = () => {
  const [autopaySettings, setAutopaySettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showSetup, setShowSetup] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [setupForm, setSetupForm] = useState({
    paymentDay: 1,
    autoAmount: '',
    paymentMethodId: '',
    backupPaymentMethodId: ''
  });

  const tenantId = localStorage.getItem('tenant_id') || sessionStorage.getItem('tenant_id');
  const isAuthenticated = !!(localStorage.getItem('tenant_token') || sessionStorage.getItem('tenant_token'));

  useEffect(() => {
    if (!isAuthenticated || !tenantId) return;

    const fetchAutopaySettings = async () => {
      try {
        setLoading(true);
        const settings = await tenantPortalAPI.getAutopaySettings();
        setAutopaySettings(settings);
      } catch (error) {
        console.error('Failed to fetch autopay settings:', error);
        setError('Failed to load autopay settings');
      } finally {
        setLoading(false);
      }
    };

    fetchAutopaySettings();
  }, [isAuthenticated, tenantId]);

  const handleSetupAutopay = async () => {
    setSaving(true);
    setError('');
    
    try {
      // In a real app, this would integrate with Stripe to create payment methods
      // For now, we'll use mock payment method setup
      const mockPaymentMethodId = 'pm_mock_' + Math.random().toString(36).substr(2, 9);
      
      const setupData = {
        payment_method_id: mockPaymentMethodId,
        auto_amount: setupForm.autoAmount ? parseFloat(setupForm.autoAmount) : null,
        payment_day: setupForm.paymentDay,
        backup_payment_method_id: setupForm.backupPaymentMethodId || null
      };

      await tenantPortalAPI.setupAutopay(setupData);
      
      // Refresh settings
      const updatedSettings = await tenantPortalAPI.getAutopaySettings();
      setAutopaySettings(updatedSettings);
      
      setShowSetup(false);
      setSuccess('Autopay has been set up successfully!');
      
      // Clear success message after 5 seconds
      setTimeout(() => setSuccess(''), 5000);
      
    } catch (error) {
      console.error('Failed to setup autopay:', error);
      setError('Failed to set up autopay. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDisableAutopay = async () => {
    if (!window.confirm('Are you sure you want to disable autopay?')) return;
    
    setSaving(true);
    setError('');
    
    try {
      await tenantPortalAPI.disableAutopay();
      
      const updatedSettings = await tenantPortalAPI.getAutopaySettings();
      setAutopaySettings(updatedSettings);
      
      setSuccess('Autopay has been disabled successfully.');
      setTimeout(() => setSuccess(''), 5000);
      
    } catch (error) {
      console.error('Failed to disable autopay:', error);
      setError('Failed to disable autopay. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="p-6 text-center">
            <CreditCard className="h-12 w-12 mx-auto mb-4 text-gray-400" />
            <p className="text-gray-600">Please sign in to manage your autopay settings.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="p-6 text-center">
            <div className="animate-spin h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading autopay settings...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Autopay Settings</h1>
          <p className="text-gray-600">Set up automatic rent payments to never miss a due date</p>
        </div>
        {!autopaySettings?.enabled && !showSetup && (
          <Button onClick={() => setShowSetup(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Set Up Autopay
          </Button>
        )}
      </div>

      {error && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-4">
            <div className="flex items-center">
              <AlertCircle className="h-5 w-5 text-red-600 mr-2" />
              <span className="text-red-800">{error}</span>
            </div>
          </CardContent>
        </Card>
      )}

      {success && (
        <Card className="border-green-200 bg-green-50">
          <CardContent className="p-4">
            <div className="flex items-center">
              <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
              <span className="text-green-800">{success}</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Current Autopay Status */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center">
              <Settings className="h-5 w-5 mr-2" />
              Autopay Status
            </div>
            <Badge variant={autopaySettings?.enabled ? 'default' : 'secondary'}>
              {autopaySettings?.enabled ? 'Active' : 'Inactive'}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {autopaySettings?.enabled ? (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center space-x-3">
                  <DollarSign className="h-5 w-5 text-green-600" />
                  <div>
                    <p className="text-sm font-medium text-gray-700">Payment Amount</p>
                    <p className="text-lg font-semibold text-gray-900">
                      ${(autopaySettings.auto_amount || 0).toLocaleString()}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-3">
                  <Calendar className="h-5 w-5 text-blue-600" />
                  <div>
                    <p className="text-sm font-medium text-gray-700">Payment Day</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {autopaySettings.payment_day}
                      {autopaySettings.payment_day === 1 ? 'st' : 
                       autopaySettings.payment_day === 2 ? 'nd' :
                       autopaySettings.payment_day === 3 ? 'rd' : 'th'} of each month
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex items-center space-x-3 p-4 bg-green-50 rounded-lg">
                <Shield className="h-5 w-5 text-green-600" />
                <div>
                  <p className="text-sm font-medium text-green-800">Payment Method</p>
                  <p className="text-sm text-green-700">
                    •••• •••• •••• {autopaySettings.payment_method_id?.slice(-4)}
                  </p>
                </div>
              </div>

              <div className="flex space-x-3 pt-4 border-t">
                <Button variant="outline" onClick={() => setShowSetup(true)}>
                  <Edit className="h-4 w-4 mr-2" />
                  Edit Settings
                </Button>
                <Button 
                  variant="outline" 
                  onClick={handleDisableAutopay}
                  disabled={saving}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Disable Autopay
                </Button>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <CreditCard className="h-16 w-16 mx-auto mb-4 text-gray-300" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Autopay Not Set Up</h3>
              <p className="text-gray-600 mb-4">
                Set up automatic payments to ensure you never miss rent due dates and avoid late fees.
              </p>
              <Button onClick={() => setShowSetup(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Set Up Autopay Now
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Autopay Setup Form */}
      {showSetup && (
        <Card>
          <CardHeader>
            <CardTitle>{autopaySettings?.enabled ? 'Edit Autopay Settings' : 'Set Up Autopay'}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="paymentDay">Payment Day of Month</Label>
                <select 
                  id="paymentDay"
                  className="w-full p-2 border border-gray-300 rounded-md"
                  value={setupForm.paymentDay}
                  onChange={(e) => setSetupForm({...setupForm, paymentDay: parseInt(e.target.value)})}
                >
                  {Array.from({length: 28}, (_, i) => i + 1).map(day => (
                    <option key={day} value={day}>
                      {day}
                      {day === 1 ? 'st' : day === 2 ? 'nd' : day === 3 ? 'rd' : 'th'} of month
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <Label htmlFor="autoAmount">Payment Amount (leave blank for full rent)</Label>
                <Input
                  id="autoAmount"
                  type="number"
                  step="0.01"
                  placeholder="Enter amount or leave blank"
                  value={setupForm.autoAmount}
                  onChange={(e) => setSetupForm({...setupForm, autoAmount: e.target.value})}
                />
              </div>
            </div>

            {/* Payment Method Setup */}
            <Card className="border-blue-200 bg-blue-50">
              <CardContent className="p-4">
                <div className="flex items-start space-x-3">
                  <CreditCard className="h-5 w-5 text-blue-600 mt-1" />
                  <div className="flex-1">
                    <h4 className="font-medium text-blue-900">Payment Method</h4>
                    <p className="text-sm text-blue-800 mt-1">
                      In a real application, this would integrate with Stripe Elements to securely collect 
                      payment method information. For this demo, autopay setup is simulated.
                    </p>
                    {/* Placeholder for Stripe Elements */}
                    <div className="mt-3 p-3 bg-white border border-blue-300 rounded-md">
                      <p className="text-sm text-gray-600">
                        🔒 Stripe Elements card input would appear here
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="flex justify-end space-x-3 pt-4 border-t">
              <Button variant="outline" onClick={() => setShowSetup(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleSetupAutopay} 
                disabled={saving}
              >
                {saving ? 'Setting up...' : autopaySettings?.enabled ? 'Update Autopay' : 'Set Up Autopay'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Benefits Card */}
      <Card className="border-green-200 bg-green-50">
        <CardContent className="p-6">
          <h3 className="font-medium text-green-900 mb-3">Benefits of Autopay</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-start space-x-2">
              <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
              <div>
                <p className="font-medium text-green-900">Never Miss a Payment</p>
                <p className="text-sm text-green-800">Automatic payments ensure you're always on time</p>
              </div>
            </div>
            <div className="flex items-start space-x-2">
              <Shield className="h-5 w-5 text-green-600 mt-0.5" />
              <div>
                <p className="font-medium text-green-900">Avoid Late Fees</p>
                <p className="text-sm text-green-800">No more worrying about due dates or late charges</p>
              </div>
            </div>
            <div className="flex items-start space-x-2">
              <CreditCard className="h-5 w-5 text-green-600 mt-0.5" />
              <div>
                <p className="font-medium text-green-900">Secure & Convenient</p>
                <p className="text-sm text-green-800">Bank-level encryption keeps your payment info safe</p>
              </div>
            </div>
            <div className="flex items-start space-x-2">
              <Calendar className="h-5 w-5 text-green-600 mt-0.5" />
              <div>
                <p className="font-medium text-green-900">Full Control</p>
                <p className="text-sm text-green-800">Change or cancel anytime through your portal</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TenantAutopay;